import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  catchError,
  // debounceTime,
  of,
  tap,
  switchMap,
  map,
  delay,
  filter,
  Subject,
  takeUntil,
} from "rxjs";
import { Descriptions } from "../constants/descriptions";
import { AssertionCriteriaType } from "../types/types";
import { assertionCriteriaDbs } from "../constants/form-constants";
import { AssertionCriteriaCitationMethodHelp } from "./AssertionCriteriaHelp";
import HelperText from "../../../components/atoms/HelperText";
import ExpandableArticle from "../../../components/Publications/components/ExpandableArticle";
import {
  Input,
  FormGroup,
  InputHelperText,
  Select,
  Label,
} from "../../../components/atoms/Form";
import Article from "../../../components/Publications/models/Article";
import { Loading } from "../../../components/atoms";
import apiClient from "../../../services/apiClient";
import rxErrorHandler from "../../../common/helpers/rxErrorHandler";

const AssertionCriteria = (props) => {
  const {
    onChangeAssertionCriteria,
    handleBlur,
    errors,
    touched,
    assertionCriteria,
  } = props;

  const { id, db, url } = assertionCriteria;
  const [selectedArticle, setSelectedArticle] = useState();
  const [pubMedError, setPubMedError] = useState();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const destroy$ = new Subject();

    const handleError = (err) => {
      console.error(err);
      setPubMedError("Invalid PubMed id");
    };

    of(null)
      .pipe(
        filter(() => id && db === "PubMed"),
        tap(() => setSelectedArticle(null)),
        tap(() => setIsLoading(true)),
        delay(1000),
        switchMap(() => apiClient.getPublication(id)),
        map((x) => x?.results || []),
        map((x) => (x ? new Article(x[0]) : null)),
        catchError(rxErrorHandler(handleError)),
        tap((x) => setSelectedArticle(x)),
        tap(() => setIsLoading(false)),
        takeUntil(destroy$)
      )
      .subscribe();

    return () => destroy$.next();
  }, [id, db]);

  const onValueChange = (e) => {
    const { name } = e.target;
    if (name === "db" || name === "id" || name === "url") {
      setPubMedError(null);
      setSelectedArticle(null);
    }
    onChangeAssertionCriteria(e);
  };

  return (
    <div className="tw-pt-8 tw-pb-8">
      <div className="tw-text-2xl">Assertion Criteria</div>
      <div className="tw-text-sm">{Descriptions.citation}</div>
      <div className="tw-pt-6">
        <div className="tw-pt-4 tw-pb-2">
          <div className="tw-text">Assertion Method Citation</div>
          <small className="text-muted form-text">
            A document describing your assertion criteria.
            <HelperText
              RenderContent={() => <AssertionCriteriaCitationMethodHelp />}
            />
          </small>
        </div>
        <FormGroup vertical={true}>
          <div className="tw-w-4/12 tw-mr-4">
            <Label text="Database" isRequired={true} />
            <Select
              name="db"
              type="select"
              disabled={url}
              onChange={(e) => onValueChange(e)}
              id="assertionCriteriaDb"
              onBlur={handleBlur}
              value={db}
            >
              <option value="" />
              {assertionCriteriaDbs.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
            {!touched.assertionCriteria.db || !errors.assertionCriteria.db ? (
              <HelperText>Clinical Database.</HelperText>
            ) : (
              <InputHelperText type="error">
                {errors.assertionCriteria.db}
              </InputHelperText>
            )}
          </div>
          <div className="tw-w-4/12 tw-mr-4">
            <Label text="Citation Id" isRequired={true} />
            <div className="tw-flex">
              <Input
                name="id"
                id="assertionCriteriaId"
                disabled={url}
                type="text"
                onBlur={handleBlur}
                value={id}
                onChange={(event) => onValueChange(event)}
              />
              {db === "PubMed" && isLoading && (
                <button type="button">{isLoading ? <Loading /> : null}</button>
              )}
            </div>
            {pubMedError && (
              <small className="form-text text-danger">{pubMedError}</small>
            )}
            {!touched.assertionCriteria.id ||
            (!errors.assertionCriteria.id && !pubMedError) ? (
              <HelperText>Database identifier.</HelperText>
            ) : (
              <InputHelperText type="error">
                {errors.assertionCriteria.id}
              </InputHelperText>
            )}
            <div className="tw-mt-2">
              <ExpandableArticle article={selectedArticle} />
            </div>
          </div>
          <div className="tw-w-4/12">
            <Label text="Citation Url" isRequired={true} />
            <Input
              id="assertionCriteriaUrl"
              name="url"
              onBlur={handleBlur}
              disabled={db || id}
              type="text"
              value={url}
              onChange={(e) => onValueChange(e)}
            />
            {touched.assertionCriteria.url &&
            errors.assertionCriteria.url &&
            db === "" &&
            id === "" ? (
              <InputHelperText type="error">
                {errors.assertionCriteria.url}
              </InputHelperText>
            ) : (
              <InputHelperText>
                The URL for a file that you have already submitted to ClinVar as
                assertion criteria.
                <HelperText customText={Descriptions.citationURL} />
              </InputHelperText>
            )}
          </div>
        </FormGroup>
      </div>
    </div>
  );
};

AssertionCriteria.propTypes = {
  onChangeAssertionCriteria: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  errors: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({}),
      PropTypes.arrayOf(PropTypes.shape({})),
    ])
  ).isRequired,
  touched: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({}),
      PropTypes.arrayOf(PropTypes.shape({})),
    ])
  ).isRequired,
  assertionCriteria: AssertionCriteriaType,
};

AssertionCriteria.defaultProps = {
  assertionCriteria: null,
};

export default AssertionCriteria;
